import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import '../styles/MapView.css';
import { useTheme } from '@mui/material/styles';
import { icons, highlightedIcons } from "./icons";
import { Paper, Typography, Box, Divider } from '@mui/material';
import API_BASE_URL from '../config';

// --- Legend Component ---
const MapLegend = ({ getSeverityColor }) => {
  const theme = useTheme();
  const riskLevels =[
    { level: 'high', label: 'High' },
    { level: 'moderate', label: 'Moderate' },
    { level: 'low', label: 'Low' },
  ];
  const assetColor = '#d73027'; // Matches the critical asset highlight color

  return (
    <Paper
      sx={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 33, 33, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        padding: '12px',
        borderRadius: '8px',
        zIndex: 1,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
    >
      {/* Assets Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box
          sx={{
            width: 30,
            height: 4,
            backgroundColor: assetColor,
            marginRight: '10px',
            borderRadius: '2px',
          }}
        />
        <Typography variant="body2">Assets within risk areas</Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Flood Risk Legend */}
      <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>Flood Risk</Typography>
      {riskLevels.map(({ level, label }) => (
        <Box key={level} sx={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: getSeverityColor(level),
              marginRight: '10px',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '3px',
            }}
          />
          <Typography variant="body2">{label}</Typography>
        </Box>
      ))}
    </Paper>
  );
};


const MapViewFlood = ({ floodData, selectedArea }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);
  const[isMapLoaded, setIsMapLoaded] = useState(false);
  const theme = useTheme();

  const getSeverityColor = useCallback((risk_level) => {
    switch (risk_level?.toLowerCase()) {
      case 'high': return '#FF0000';
      case 'moderate': return '#FFA500';
      case 'low': return '#FFFF00';
      default: return '#D3D3D3';
    }
  },[]);

  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded() || !selectedArea) return;

    const geometry = selectedArea.geometry;
    if (!geometry || geometry.type !== 'Polygon' || !geometry.coordinates[0]) return;

    const coordinates = geometry.coordinates[0];
    const bounds = coordinates.reduce(
      (b, coord) => b.extend(coord),
      new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
    );

    map.current.fitBounds(bounds, {
      padding: 100,
      maxZoom: 16,
      essential: true,
    });
  }, [selectedArea]);

  const addIconToMap = useCallback((iconUrl, iconId) => {
    if (!map.current) return Promise.reject(new Error("Map is not initialized"));
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = iconUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');

        if (map.current) {
          map.current.loadImage(pngUrl, (error, image) => {
            if (error) {
              reject(error);
            } else if (map.current) {
              if (map.current.hasImage(iconId)) map.current.removeImage(iconId);
              map.current.addImage(iconId, image, { sdf: false });
              resolve();
            } else {
              reject(new Error("Map uninitialized during image loading"));
            }
          });
        } else {
          reject(new Error("Map uninitialized during image processing"));
        }
      };
      img.onerror = reject;
    });
  },[]);

  const createFloodPopupContent = useCallback((properties) => {
    const riskLevel = properties.risk_level || 'N/A';
    return `
      <div class="popup-container">
        <div class="popup-body">
          <p class="popup-detail">
            <strong>Flood Risk Level:</strong>
            <span style="font-weight: bold;">${riskLevel}</span>
          </p>
        </div>
      </div>
    `;
  },[]);

  const addPopupEventListeners = useCallback(() => {
    if (!map.current) return;

    if (!popup.current) {
      popup.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'custom-popup'
      });
    }

    const handleMouseEnter = (e) => {
      map.current.getCanvas().style.cursor = 'pointer';
      const coordinates = e.lngLat;
      const properties = e.features[0].properties;
      popup.current.setLngLat(coordinates).setHTML(createFloodPopupContent(properties)).addTo(map.current);
    };

    const handleMouseLeave = () => {
      map.current.getCanvas().style.cursor = '';
      popup.current.remove();
    };

    const layerId = 'flood-areas';
    if (map.current.getLayer(layerId)) {
      map.current.off('mouseenter', layerId, handleMouseEnter);
      map.current.off('mouseleave', layerId, handleMouseLeave);
      map.current.on('mouseenter', layerId, handleMouseEnter);
      map.current.on('mouseleave', layerId, handleMouseLeave);
    }
  }, [createFloodPopupContent]);

  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
      center:[18.0656, 59.3327],
      zoom: 12,
    });

    map.current.on('load', () => setIsMapLoaded(true));

    return () => {
      setIsMapLoaded(false);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  },[]);

  useEffect(() => {
    if (map.current && isMapLoaded) {
      const newStyle = theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10';
      map.current.setStyle(newStyle);
      map.current.once('styledata', () => {
        setIsMapLoaded(false);
        setTimeout(() => setIsMapLoaded(true), 100);
      });
    }
  }, [theme.palette.mode]);

  useEffect(() => {
    if (isMapLoaded && floodData) {
      const addLayers = () => {
        const layersToRemove = ['network-nodes', 'network-lines', 'flood-areas'];
        const sourcesToRemove =['network-tiles', 'flood-data'];
        layersToRemove.forEach(id => { if (map.current.getLayer(id)) map.current.removeLayer(id); });
        sourcesToRemove.forEach(id => { if (map.current.getSource(id)) map.current.removeSource(id); });

        map.current.addSource('network-tiles', {
          type: 'vector',
          tiles:[`${API_BASE_URL}/api/tiles/combined_network/{z}/{x}/{y}.pbf`],
          minzoom: 0,
          maxzoom: 13
        });

        if (map.current.getSource('flood-data')) {
            map.current.getSource('flood-data').setData(floodData);
        } else {
            map.current.addSource('flood-data', { type: 'geojson', data: floodData });
        }

        if (!map.current.getLayer('flood-areas')) {
          map.current.addLayer({
            id: 'flood-areas',
            type: 'fill',
            source: 'flood-data',
            paint: {
              'fill-color':[
                'match',
                ['get', 'risk_level'],
                'High', getSeverityColor('high'),
                'Moderate', getSeverityColor('moderate'),
                'Low', getSeverityColor('low'),
                getSeverityColor('default')
              ],
              'fill-opacity': 0.5
            }
          });
        }

        const SOURCE_LAYER_NAME = 'combined_network';

        map.current.addLayer({
          id: 'network-lines',
          type: 'line',
          source: 'network-tiles',
          'source-layer': SOURCE_LAYER_NAME,
          filter: ['==', '$type', 'LineString'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['case',['==', ['get', 'is_critical'], true], '#d73027', theme.palette.mode === 'dark' ? '#555' : '#D3D3D3'],
            'line-width': ['case', ['==', ['get', 'is_critical'], true], 2.5, 1],
          },
        });

        const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;

        map.current.addLayer({
          id: 'network-nodes',
          type: 'symbol',
          source: 'network-tiles',
          'source-layer': SOURCE_LAYER_NAME,
          filter: ['all',['==', '$type', 'Point'], ['!=', 'name', 'Road']],
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'is_critical'], true],
              ['match', ['get', 'name'], 'School', 'school-highlighted', 'Port', 'directions-boat-highlighted', 'Harbour', 'directions-boat-highlighted', 'Household', 'home-highlighted', 'Supermarket', 'local-grocery-store-highlighted', 'Warehouse', 'warehouse-highlighted', 'Farm', 'agriculture-highlighted', 'Power station', 'factory-highlighted', 'Power substation', 'power-highlighted', 'Hospital', 'local-hospital-highlighted', 'Clinic', 'local-hospital-highlighted', 'Pharmacy', 'pharmacy-highlighted', 'Fire station', 'fire-station-highlighted', 'Police station', 'police-highlighted', 'Military base', 'police-highlighted', 'Train station', 'train-highlighted', 'Airport', 'plane-highlighted', 'Government office', 'office-highlighted', 'Courthouse', 'office-highlighted', 'City hall', 'office-highlighted', 'Communication tower', 'communication-tower-highlighted', 'Water reservoir', 'reservoir-highlighted', 'Water plant', 'droplet-highlighted', 'Wastewater plant', 'droplet-highlighted', 'home-highlighted'],
              ['match', ['get', 'name'], 'School', getIconName('school'), 'Port', getIconName('directions-boat'), 'Harbour', getIconName('directions-boat'), 'Household', getIconName('home'), 'Supermarket', getIconName('local-grocery-store'), 'Warehouse', getIconName('warehouse'), 'Farm', getIconName('agriculture'), 'Power station', getIconName('factory'), 'Power substation', getIconName('power'), 'Hospital', getIconName('local-hospital'), 'Clinic', getIconName('local-hospital'), 'Pharmacy', getIconName('pharmacy'), 'Fire station', getIconName('fire-station'), 'Police station', getIconName('police'), 'Military base', getIconName('police'), 'Train station', getIconName('train'), 'Airport', getIconName('plane'), 'Government office', getIconName('office'), 'Courthouse', getIconName('office'), 'City hall', getIconName('office'), 'Communication tower', getIconName('communication-tower'), 'Water reservoir', getIconName('reservoir'), 'Water plant', getIconName('droplet'), 'Wastewater plant', getIconName('droplet'), getIconName('home')]
            ],
            'icon-size':['case', ['==', ['get', 'is_critical'], true], 1.1, 1],
            'icon-allow-overlap': true,
          },
          minzoom: 12
        });

        addPopupEventListeners();
      };

      const allIcons = { ...icons, ...highlightedIcons };
      const iconPromises = Object.entries(allIcons).map(([name, url]) => addIconToMap(url, name));

      Promise.all(iconPromises).then(addLayers).catch(error => console.error("Failed to load map icons:", error));
    }
  },[isMapLoaded, floodData, theme.palette.mode, addIconToMap, addPopupEventListeners, getSeverityColor]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '540px' }}>
      <div
        ref={mapContainer}
        style={{ width: '100%', height: '100%', borderRadius: '8px' }}
      />
      <MapLegend getSeverityColor={getSeverityColor} />
    </div>
  );
};

export default MapViewFlood;